import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Printer, Cake, DollarSign } from "lucide-react";
import { api, getAdminCode } from "@/lib/api";

export default function PosterPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [posterUrl, setPosterUrl] = useState(() => window.location.origin);
  const [headline, setHeadline] = useState("Order your custom sweets");
  const [subhead, setSubhead] = useState("Scan to book cakes, cupcakes, cake pops & more");

  useEffect(() => {
    if (!getAdminCode()) {
      navigate("/");
      return;
    }
    api.get("/config").then((r) => {
      setConfig(r.data);
      if (r.data?.public_site_url) setPosterUrl(r.data.public_site_url);
    }).catch((e) => console.error("config load failed", e));
  }, [navigate]);

  const handlePrint = () => window.print();

  if (!config) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;

  const cashTag = (config.cashapp_tag || "").replace(/^\$/, "");

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="print:hidden bg-white border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-stone-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4" /> Back to admin
          </button>
          <Button onClick={handlePrint} data-testid="poster-print-btn" className="btn-pill btn-navy border-0 gap-2">
            <Printer className="h-4 w-4" /> Print poster
          </Button>
        </div>
      </div>

      <div className="print:hidden max-w-5xl mx-auto px-6 py-8 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 bg-white rounded-2xl border border-stone-100 p-5 space-y-4 h-fit">
          <p className="text-xs uppercase tracking-widest text-stone-400">Customize</p>
          <div>
            <Label className="text-xs text-stone-500">QR link</Label>
            <Input value={posterUrl} onChange={(e) => setPosterUrl(e.target.value)} data-testid="poster-url-input" />
            <p className="text-xs text-stone-400 mt-1">Defaults to your booking site. You can point it to a specific page or menu section.</p>
          </div>
          <div>
            <Label className="text-xs text-stone-500">Headline</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-stone-500">Subheading</Label>
            <Input value={subhead} onChange={(e) => setSubhead(e.target.value)} />
          </div>
          <p className="text-xs text-stone-400 pt-2 border-t border-stone-100">
            Tip: click <span className="font-medium">Print poster</span> to save as PDF or print letter/A4 for the shop, market, or event table.
          </p>
        </div>

        <div className="md:col-span-2">
          <Poster
            config={config}
            posterUrl={posterUrl}
            headline={headline}
            subhead={subhead}
            cashTag={cashTag}
          />
        </div>
      </div>

      <div className="hidden print:block">
        <Poster
          config={config}
          posterUrl={posterUrl}
          headline={headline}
          subhead={subhead}
          cashTag={cashTag}
          printMode
        />
      </div>

      <style>{`
        @media print {
          @page { size: letter; margin: 0.4in; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
        }
      `}</style>
    </div>
  );
}

function Poster({ config, posterUrl, headline, subhead, cashTag, printMode = false }) {
  return (
    <div
      data-testid="poster-preview"
      className={`relative overflow-hidden rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-amber-50/60 aspect-[8.5/11] ${printMode ? "" : "shadow-[0_20px_60px_-20px_rgba(15,44,85,0.25)]"} px-10 py-12`}
    >
      <div className="absolute top-8 right-10 w-40 h-40 rounded-full bg-amber-200/40 blur-3xl" />
      <div className="absolute bottom-8 left-10 w-52 h-52 rounded-full bg-sky-300/40 blur-3xl" />

      <div className="relative h-full flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <img src="/cakesbyky-logo.png?v=2" alt={config.business_name} className="h-14 w-auto object-contain" />
          {config.tagline && (
            <p className="text-xs uppercase tracking-widest text-[color:var(--brand-navy)]/70 mt-1">{config.tagline}</p>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-amber-600 mb-4">Scan me</p>
          <h1 className="font-serif-display text-5xl leading-tight text-[color:var(--brand-navy)] mb-3 max-w-md">{headline}</h1>
          <p className="text-slate-600 mb-8 max-w-md">{subhead}</p>
          <div className="p-5 bg-white rounded-3xl shadow-[0_10px_40px_-10px_rgba(15,44,85,0.25)]">
            <QRCodeSVG
              value={posterUrl}
              size={220}
              level="H"
              fgColor="#0F766E"
              bgColor="#FFFFFF"
              includeMargin={false}
            />
          </div>
          <p className="mt-4 text-xs text-stone-400 font-mono break-all max-w-md">{posterUrl}</p>
        </div>

        <div className="border-t border-amber-100 pt-4 mt-4 grid grid-cols-3 gap-3 text-xs text-stone-600">
          <div>
            <p className="uppercase tracking-widest text-stone-400 mb-1">Contact</p>
            {config.email && <p>{config.email}</p>}
            {config.phone && <p>{config.phone}</p>}
          </div>
          <div>
            <p className="uppercase tracking-widest text-stone-400 mb-1">Follow</p>
            {config.instagram && <p>@{config.instagram.replace("@", "")}</p>}
          </div>
          <div>
            <p className="uppercase tracking-widest text-stone-400 mb-1">Pay via</p>
            {cashTag ? (
              <p className="flex items-center gap-1"><DollarSign className="h-3 w-3 text-[#00D632]" />${cashTag}</p>
            ) : (
              <p className="text-stone-400">Add your $cashtag</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
